import { textRenderer } from 'handsontable/renderers'
import type { QueuedResolution, List } from './types'
import { t } from '@lingui/core/macro'
import moment from 'moment'
import type { HotTable } from '@handsontable/react'
import type { ThreadData, CommentBodyInlineElement, CommentBodyParagraph } from '@liveblocks/client'
import { useThreads } from '@liveblocks/react/suspense'
import { useEffect } from 'react'
import { ModifyPositionFunc } from '@components/tooltip/tooltip'

export const getListValueId = (list: List, row, col) => {
  const listValues = list?.list_columns?.[col]?.list_values
  return listValues?.find(val => val.row_number === row)?.id?.toString() ?? null
}

export const getListValueText = (list: List, listValueId: string): string | null => {
  const allValues = (list.list_columns || []).flatMap(col => col.list_values || [])
  const match = allValues.find(val => val.id?.toString() === listValueId)
  return match?.value ?? null
}

export const getSafeDialogPosition = (buttonRect, offset = 10) => {
  const dialogWidth = 400
  const dialogHeight = 200

  const { scrollY } = window
  const { scrollX } = window
  const viewportBottom = window.innerHeight + scrollY
  const viewportRight = window.innerWidth + scrollX

  let top = buttonRect.top + scrollY + offset
  let left = buttonRect.left + scrollX + offset

  // Adjust if too close to bottom
  const spaceBelow = viewportBottom - (buttonRect.bottom + scrollY)
  if (spaceBelow < dialogHeight && spaceBelow > 10) {
    top = buttonRect.bottom + scrollY + Math.min(spaceBelow - dialogHeight, offset)
  } else if (spaceBelow <= 10) {
    top = buttonRect.top + scrollY - dialogHeight - offset
  }

  // Adjust if too close to right edge
  if (left + dialogWidth > viewportRight) {
    left = buttonRect.left + scrollX - dialogWidth - offset
  }
  return { top, left }
}

export const getCommentText = (thread: ThreadData): string => {
  const bodyContent = thread.comments[0]?.body?.content
  if (!bodyContent || !Array.isArray(bodyContent)) return ''

  const extractText = (nodes: (CommentBodyParagraph | CommentBodyInlineElement)[]): string =>
    nodes
      .map(node => {
        if ('text' in node && typeof node.text === 'string') return node.text
        if ('children' in node && Array.isArray(node.children)) return extractText(node.children)
        return ''
      })
      .join('\n')

  return extractText(bodyContent)
}

const ADD_COMMENT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
  <path d="M1 9.5V15C1 16.1046 1.89543 17 3 17H10C14.4183 17 18 13.4183 18 9C18 4.58172 14.4183 1 10 1H9.5C4.80558 1 1 4.80558 1 9.5Z" stroke="#4F6D76" stroke-width="1.5"/>
  <path d="M9.09961 7.25V11.45" stroke="#4F6D76" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M7 9.34998H11.2" stroke="#4F6D76" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

const HAS_COMMENT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
  <path d="M0 8.5V14C0 15.1046 0.89543 16 2 16H9C13.4183 16 17 12.4183 17 8C17 3.58172 13.4183 0 9 0H8.5C3.80558 0 0 3.80558 0 8.5Z" fill="#0092F3"/>
  <path d="M8.5 5.5V9" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
  <circle cx="8.5" cy="11.2" r="0.75" fill="white"/>
</svg>
`

const COMMENT_APPROVED = `<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
  <circle cx="8.5" cy="8.5" r="8.5" fill="#4AA97E"/>
  <path d="M1 3.5L3.33333 6L8 1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" transform="translate(4 5)"/>
</svg>`

const COMMENT_REJECTED = `<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
  <circle cx="8.5" cy="8.5" r="8.5" fill="#E41B0C" />
  <path d="M5.5 5.5L11.5 11.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M5.5 11.5L11.5 5.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`

export const makeButtonRenderer =
  (
    list: List,
    activeCellId: string | null,
    threads: ThreadData[] | undefined,
    queuedResolutions: Map<string, QueuedResolution> = new Map(),
  ) =>
  (hot, td, row, col, prop, val, cellProps) => {
    // render base cell content (text value)
    textRenderer(hot, td, row, col, prop, val, cellProps)

    // set up cell styles for proper text truncation and button alignment
    td.style.cssText = `
      position: relative;
      padding: 4px 32px 4px 8px !important;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `

    // remove old button if exists
    const existingButton = td.querySelector('.cell-button')
    if (existingButton) existingButton.remove()

    const listValueId = getListValueId(list, row, col)
    if (!listValueId) return td

    const currentValue = getListValueText(list, listValueId)
    const hasPendingThread = threads?.some(
      t => t.metadata?.listValueId === listValueId && t.metadata?.status === 'pending',
    )

    // Check if this thread is queued for resolution
    const hasRejectedChanges = threads?.some(t => {
      const isQueued = queuedResolutions.has(t.id)
      const queuedStatus = queuedResolutions.get(t.id)?.status
      return (
        t.metadata?.listValueId === listValueId &&
        ((t.metadata?.status === 'rejected' && !t.resolved) ||
          (isQueued && queuedStatus === 'rejected'))
      )
    })

    const hasApprovedChanges = threads?.some(t => {
      const isQueued = queuedResolutions.has(t.id)
      const queuedStatus = queuedResolutions.get(t.id)?.status
      return (
        t.metadata?.listValueId === listValueId &&
        ((t.metadata?.status === 'approved' && !t.resolved) ||
          (isQueued && queuedStatus === 'approved'))
      )
    })

    // Don't show comment button on empty cells
    if (!currentValue && !hasPendingThread) {
      return td
    }

    const btn = document.createElement('button')
    btn.className = 'cell-button'
    btn.style.cssText = `
      position: absolute;
      right: 4px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 0;
    `

    if (hasRejectedChanges) {
      btn.innerHTML = COMMENT_REJECTED
      btn.disabled = true
      btn.style.cursor = 'default'
      td.appendChild(btn)
    } else if (hasApprovedChanges) {
      btn.innerHTML = COMMENT_APPROVED
      btn.disabled = true
      btn.style.cursor = 'default'
      td.appendChild(btn)
    } else if (hasPendingThread) {
      btn.innerHTML = HAS_COMMENT_SVG
      td.appendChild(btn)
    } else if (activeCellId === listValueId) {
      btn.innerHTML = ADD_COMMENT_SVG
      td.appendChild(btn)
    }
    return td
  }

export const formatRelativeTime = date => {
  const targetDate = moment(date)
  const today = moment()
  const yesterday = moment().subtract(1, 'day')

  const FORMAT_TIME = 'h:mma'
  const FORMAT_DATETIME_SHORT = 'h:mma MMM D'

  const formatted = targetDate.format(FORMAT_TIME)
  if (targetDate.isSame(today, 'day')) {
    return t`${formatted} Today`
  }
  if (targetDate.isSame(yesterday, 'day')) {
    return t`${formatted} Yesterday`
  }
  return targetDate.format(FORMAT_DATETIME_SHORT)
}

export const formatInitials = (name = t`Not Assigned`) =>
  name
    .split(' ')
    .slice(0, 2)
    .filter(substr => substr)
    .map(substr => substr[0]?.toUpperCase())
    .join('')

export const updateCellWithApprovedComment = (
  thread: ThreadData,
  tableRef: React.RefObject<HotTable>,
  list: List,
): void => {
  const approvedText = getCommentText(thread)
  if (!approvedText || !tableRef?.current?.hotInstance) return

  const hot = tableRef.current.hotInstance
  const coords = hot
    ?.getData()
    ?.flatMap((row, rIdx) =>
      row.map((_, cIdx) => {
        const val = getListValueId(list, rIdx, cIdx)
        return val === thread.metadata.listValueId ? { r: rIdx, c: cIdx } : null
      }),
    )
    .find(Boolean)

  if (coords) {
    hot.setDataAtCell(coords.r, coords.c, approvedText)
  }
}

export const useFetchAllThreads = (options?: Parameters<typeof useThreads>[0]) => {
  const { threads, fetchMore, isFetchingMore, hasFetchedAll } = useThreads(options)

  useEffect(() => {
    if (!isFetchingMore && !hasFetchedAll) {
      fetchMore()
    }
  }, [isFetchingMore, hasFetchedAll, fetchMore])

  return threads
}

export const calculateTooltipOffset: ModifyPositionFunc = (
  position,
  triggerElement,
  triggerElementInner,
) => ({
  ...position,
  left:
    position.left -
    (triggerElement?.getBoundingClientRect().width ?? 0) / 2 +
    (triggerElementInner?.getBoundingClientRect().width ?? 0) / 2,
})
